import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, forwardRef, useImperativeHandle, useState, } from "react";
import { Engine, Scene, Vector3, StandardMaterial, Color3, ArcRotateCamera, PBRMaterial, SceneLoader, HighlightLayer, } from "@babylonjs/core";
import "@babylonjs/loaders/glTF";
import "@babylonjs/loaders/OBJ";
import { createCamera, createBuilding, createPoint, centralToast, createPipeline, toggleNodeMesh, filterUVMesh, filterMesh, filterNode, createTexture, createMesh, preloadImages, } from "@/utils/sceneHandler";
import axios from "axios";
const AssetMap3D = forwardRef(({ className = "", jsonUrl = "", onLoad, onBuildingEnter, onBuildingLeave, onBuildingClick, onCameraFocus, onCameraBlur, onPointClick, onPointEnter, onViewMatrixChangedObservable, onProjectionMatrixChangedObservable, }, ref) => {
    useImperativeHandle(ref, () => ({
        cameraBlur: () => {
            if (activeCamera.current.isLock)
                activeCamera.current.blur();
        },
        cameraFocus: (building, cameraFocusSetting) => {
            if (!activeCamera.current.isLock)
                activeCamera.current.focus(building, cameraFocusSetting);
        },
        stopRenderLoop: () => {
            stopRenderLoop();
        },
        renRenderLoop: () => {
            renRenderLoop();
        },
    }));
    const canvasRef = useRef(null);
    const engineRef = useRef(null);
    const sceneRef = useRef(null);
    const currentBuilding = useRef(null);
    const activeCamera = useRef(null);
    const [cameraAlpha, setCameraAlpha] = useState(0);
    const [compassVisible, setCompassVisible] = useState(false);
    const renRenderLoop = () => {
        stopRenderLoop();
        engineRef?.current?.runRenderLoop(() => sceneRef?.current?.render()); // 恢复渲染
    };
    const stopRenderLoop = () => {
        engineRef?.current?.stopRenderLoop();
    };
    const handleResize = () => {
        engineRef?.current?.resize();
    };
    const currentBuildingList = useRef(null);
    useEffect(() => {
        const loadMesh = function (data, scene, toast) {
            return new Promise((resolve, reject) => {
                let { meshName, rootUrl, fileName } = data;
                let url = window.location.origin + "/glb/";
                let { total } = data.scene || { total: 33071560 };
                toast.setContent("加载中···");
                SceneLoader.ImportMesh(meshName, process.env.REACT_APP_ENV != "development" ? url : rootUrl, fileName, scene, (meshes) => {
                    toast.clearContent();
                    resolve(meshes);
                }, (event) => {
                    const percent = Math.round((event.loaded / total) * 100);
                    if (percent >= 99) {
                        toast.setContent("加载完成，请稍后···");
                    }
                    else {
                        toast.setContent("模型加载: " + percent + "%");
                    }
                }, (err) => {
                    toast.setContent("加载模型失败。");
                    reject(err);
                });
            });
        };
        const runAsync = async () => {
            if (!canvasRef.current)
                return;
            const envPrefix = process.env.REACT_APP_ENV !== "development" ? "/datav/" : "";
            const engine = new Engine(canvasRef.current, true, {
                preserveDrawingBuffer: false,
                stencil: true,
                adaptToDeviceRatio: true,
            });
            engineRef.current = engine;
            renRenderLoop();
            window.addEventListener("resize", handleResize);
            const scene = new Scene(engine);
            sceneRef.current = scene;
            const camera = new ArcRotateCamera("camera", 0, Math.PI / 2, 10, Vector3.Zero(), scene);
            if (canvasRef.current)
                camera.attachControl(canvasRef.current, true);
            const highlightLayer = new HighlightLayer("highlightLayer", scene);
            highlightLayer.blurHorizontalSize = 1.5;
            highlightLayer.blurVerticalSize = 1.5;
            const toast = new centralToast();
            const json = await axios.get(process.env.REACT_APP_ENV != "development"
                ? "/datav/" + jsonUrl
                : jsonUrl);
            const sceneData = json.data;
            const { materialsFilter = [], hideMeshes = [], scaling = {}, meshStandardMaterial = [], bgColor = "#48486f", compass = false, compassStart, } = sceneData.scene || {};
            const { buildings = [], points = [], pipelines = [] } = sceneData;
            let sources = buildings
                .map((d) => d.textures || [])
                .flat()
                .map((d) => d.source);
            toast.setContent("加载中···");
            let updateProgress = function (loaded, total, results) {
                const percentage = Math.round((loaded / total) * 100);
                toast.setContent("贴图加载: " + percentage + "%");
            };
            await preloadImages(sources, updateProgress);
            const meshes = (await loadMesh(sceneData.scene, scene, toast));
            scene.getEngine().resize();
            const loadData = (await onLoad?.(engineRef.current, sceneRef.current)) || {};
            var rootMesh = meshes[0];
            rootMesh.scaling = new Vector3(scaling?.x ?? 1, scaling?.y ?? 1, scaling?.z ?? -1);
            rootMesh.bakeCurrentTransformIntoVertices();
            scene.createDefaultCameraOrLight(true, true, true);
            const envConfig = {
                ...sceneData.scene, // 保留你json里scene的其他配置（天空盒开关、地面开关等）
                // 强制指定本地静态资源路径
                skyboxTexture: envPrefix + "assets/environments/backgroundSkybox.dds",
                groundTexture: envPrefix + "assets/environments/backgroundGround.png",
                environmentTexture: envPrefix + sceneData.scene.environmentTexture,
            };
            // 使用本地文件创建默认环境
            scene.createDefaultEnvironment(envConfig);
            // scene.createDefaultEnvironment(sceneData.scene || {});
            scene.onBeforeRenderObservable.add(() => {
                let carema = scene?.activeCamera;
                let { alpha } = carema;
                let start = compassStart ? Number(compassStart) : 0;
                setCameraAlpha(start + (alpha * 180) / Math.PI);
            });
            setCompassVisible(compass);
            scene.ambientColor = new Color3(1, 1, 1);
            scene.materials
                .filter((a) => materialsFilter.map((b) => b.name).includes(a.name))
                .forEach((material) => {
                let data = materialsFilter.find((d) => d.name == material.name) || {};
                let { color, uniqueId, name, ...option } = data;
                if (color && material instanceof StandardMaterial) {
                    material.reflectionTexture = null;
                    let srgbColor = Color3.FromHexString(color);
                    let linearColor = srgbColor.toLinearSpace();
                    material.specularColor = linearColor;
                }
                if (color && material instanceof PBRMaterial) {
                    material.reflectionTexture = null;
                    let srgbColor = Color3.FromHexString(color);
                    let linearColor = srgbColor.toLinearSpace();
                    material.emissiveColor = linearColor;
                }
                Object.keys(option).forEach((key) => (material[key] = option[key]));
            });
            (function (meshes1 = [], meshes2 = []) {
                const combined = meshes1.concat(meshes2);
                const meshes3 = [], meshes4 = [];
                combined.forEach((option) => {
                    if (typeof option === "string")
                        meshes3.push(option);
                    else
                        meshes4.push(option);
                });
                meshes
                    .filter((d) => meshes3.includes(d.name))
                    .forEach((mesh) => (mesh.isVisible = false));
                meshes4.forEach((option) => filterNode(option, scene)?.forEach((node) => toggleNodeMesh(node, false)));
            })(hideMeshes, loadData.hideMeshes);
            meshStandardMaterial.forEach((d) => {
                let { name, color, type = "StandardMaterial", ...option } = d;
                let mesh = meshes.find((d) => d.name == name);
                if (!mesh)
                    return false;
                let material;
                if (color && type == "StandardMaterial") {
                    material = new StandardMaterial("meshStandardMaterial", scene);
                    material.diffuseColor = Color3.FromHexString(color);
                }
                if (color && type == "PBRMaterial") {
                    const material = new PBRMaterial("meshPBRMaterial", scene);
                    material.reflectionTexture = null;
                    let srgbColor = Color3.FromHexString(color);
                    let linearColor = srgbColor.toLinearSpace();
                    material.emissiveColor = linearColor;
                }
                if (!material)
                    return false;
                Object.keys(option).forEach((key) => (material[key] = option[key]));
                mesh.material = material;
            });
            let lastFocusHideMesh = null;
            activeCamera.current = new createCamera(scene, sceneData.camera);
            activeCamera.current.onFocus = function () {
                if (!currentBuilding.current)
                    return false;
                let { cameraFocusSetting = {} } = currentBuilding.current;
                let { useHighlight = true, hideOther = false, hideMeshes = [], points = [], } = cameraFocusSetting;
                currentBuilding.current.building.toggleHighlight(useHighlight);
                if (hideOther)
                    buildingList
                        .filter((a, b) => b != currentBuilding.current.index)
                        .forEach((b) => b.building.setEnabled(false));
                let current = buildingList[currentBuilding.current.index];
                let building = current.building;
                let buildingInfo = {
                    id: current.id,
                    x: building.position.x,
                    y: building.position.y,
                    z: building.position.z,
                    name: current.name,
                };
                if (building.box)
                    building.box.isVisible = false;
                if (hideMeshes.length) {
                    lastFocusHideMesh = hideMeshes
                        .map((data) => {
                        let { type, ...option } = data;
                        if (type == "node") {
                            let nodes = filterNode(option, scene);
                            if (nodes?.length)
                                nodes.forEach((node) => toggleNodeMesh(node, false));
                            return nodes;
                        }
                        else {
                            let meshes = filterMesh(option, scene);
                            if (meshes?.length)
                                meshes.forEach((node) => toggleNodeMesh(node, false));
                            return meshes;
                        }
                    })
                        .flat();
                }
                if (points.length) {
                    current.points = getPointsList(points, building.position, buildingInfo);
                }
                scene.getEngine().resize();
                onCameraFocus?.(buildingInfo);
            };
            activeCamera.current.onBlur = function () {
                if (!currentBuilding.current)
                    return false;
                let { cameraFocusSetting = {} } = currentBuilding.current;
                let { useHighlight = true, hideOther = false, hideMeshes = [], } = cameraFocusSetting;
                if (currentBuilding.current.building.box)
                    currentBuilding.current.building.box.isVisible = true;
                if (useHighlight)
                    currentBuilding.current.building.toggleHighlight(false);
                if (hideOther)
                    buildingList.forEach((b) => b.building.setEnabled(true));
                let current = buildingList[currentBuilding.current.index];
                current.points.forEach((item) => item.dispose());
                //current.points = [];
                if (current.subMeshes.length) {
                    current.subMeshes.forEach((m) => m.toggleHighlight(false));
                }
                if (hideMeshes.length && lastFocusHideMesh) {
                    lastFocusHideMesh.forEach((mesh) => toggleNodeMesh(mesh, true));
                    lastFocusHideMesh = null;
                }
                scene.getEngine().resize();
                onCameraBlur?.();
            };
            activeCamera.current.onViewMatrixChangedObservable = function (e) {
                onViewMatrixChangedObservable?.(e);
            };
            activeCamera.current.onProjectionMatrixChangedObservable = function (e) {
                onProjectionMatrixChangedObservable?.(e);
            };
            const getPointsList = (points = [], position, buildingInfo = {}) => {
                let pointsList = points.map(({ offsetX = 0, offsetY = 0, offsetZ = 0, id = "", name = "", data = undefined, source, activeSource, ...option }) => {
                    let [pointX, pointY, pointZ] = [
                        position.x + offsetX,
                        position.y + offsetY,
                        position.z + offsetZ,
                    ];
                    // 资源路径拼接前缀
                    const pointOpts = {
                        x: pointX,
                        y: pointY,
                        z: pointZ,
                        ...option,
                        source: envPrefix + source,
                        activeSource: envPrefix + activeSource,
                    };
                    let point = new createPoint(name, scene, pointOpts, {
                        name,
                        parent,
                    });
                    highlightLayer.addExcludedMesh(point.textPlane);
                    highlightLayer.addExcludedMesh(point.box);
                    point.id = id;
                    point.onClick = function (pointInfo = {}) {
                        pointsList.forEach((t) => t.setActive(false));
                        point.setActive(true);
                        onPointClick?.(Object.assign(buildingInfo, {
                            pointX,
                            pointY,
                            pointZ,
                            pointName: name,
                            pointId: id,
                            pointData: data,
                        }), pointInfo);
                    };
                    return point;
                });
                return pointsList;
            };
            const getTextureList = (textures = [], building) => {
                let { position, mesh } = building;
                return textures.map(({ offsetX = 0, offsetY = 0, offsetZ = 0, source, ...option }) => {
                    let [pointX, pointY, pointZ] = [
                        position.x + offsetX,
                        position.y + offsetY,
                        position.z + offsetZ,
                    ];
                    const texOpts = {
                        x: pointX,
                        y: pointY,
                        z: pointZ,
                        ...option,
                        source: envPrefix + source,
                    };
                    let point = new createTexture(scene, texOpts, mesh);
                    highlightLayer.addExcludedMesh(point.textPlane);
                    return point;
                });
            };
            const getPipelineList = (pipelines = []) => {
                let pipelineList = pipelines.map((data) => {
                    let { id, name, cameraFocusSetting, emitters = [], tubeOption = {}, auxiliary = false, } = data;
                    let pipeline = new createPipeline(meshes.filter((mesh) => filterUVMesh(mesh, data)), emitters, tubeOption, scene, activeCamera.current, highlightLayer, auxiliary);
                    if (pipeline) {
                        pipeline.mouseEnter = function () {
                            onBuildingEnter?.(name, id);
                        };
                        pipeline.mouseLeave = function () {
                            onBuildingLeave?.(name, id);
                        };
                        pipeline.onClick = function () {
                            pipeline.toggleActive();
                            pipelineList.forEach((t) => t.pipeline != pipeline && t.pipeline.setActive(false));
                            if (pipeline.active) {
                                onBuildingClick?.(name, id);
                            }
                            else {
                                onBuildingClick?.(null, null);
                            }
                        };
                        pipeline.setActive(auxiliary);
                    }
                    return { id, name, pipeline };
                });
                return pipelineList;
            };
            const getSubmeshes = (subMeshes, building, buildingInfo) => {
                let subMeshesList = [], { mesh: parent } = building;
                let getMesh = (meshes, data, index = 0) => {
                    let mesh = new createMesh(meshes, scene, activeCamera.current, highlightLayer);
                    if (!mesh)
                        return mesh;
                    let oParent = undefined, oName = undefined;
                    if (mesh.mesh) {
                        oName = mesh.mesh.name;
                        oParent = mesh.mesh.parent?.name;
                        mesh.mesh.setParent(parent, true);
                    }
                    mesh.useHighlight(true);
                    mesh.mouseEnter = function () {
                        onPointEnter?.(oParent, index);
                    };
                    mesh.mouseLeave = function () {
                        onPointEnter?.();
                    };
                    mesh.onClick = function (pointInfo = {}) {
                        subMeshesList.forEach((m) => m.toggleHighlight(false));
                        mesh.toggleHighlight(true);
                        onPointClick?.(Object.assign(buildingInfo, {
                            pointId: index,
                            pointName: oParent,
                        }), pointInfo);
                    };
                    return mesh;
                };
                subMeshesList = subMeshes
                    .map((data) => {
                    let { merge = true, ...option } = data;
                    let subMesh = meshes.filter((mesh) => filterUVMesh(mesh, option));
                    if (!merge)
                        return subMesh.map((mesh, index) => getMesh([mesh], option, index));
                    else
                        return getMesh(subMesh, option);
                })
                    .flat();
                return subMeshesList;
            };
            let buildingList = buildings.map((data, index) => {
                let { id, name, cameraFocusSetting, useHighlight = true, createBox, textures = [], subMeshes = [], } = data;
                let building;
                try {
                    building = new createBuilding(meshes.filter((mesh) => filterUVMesh(mesh, data)), scene, activeCamera.current, highlightLayer, createBox);
                }
                catch (error) {
                    console.error("Error merging meshes:", error);
                    console.error(meshes.filter((mesh) => filterUVMesh(mesh, data)).map((d) => d));
                }
                if (building) {
                    building.useHighlight(useHighlight);
                    let mesh = building.mesh;
                    if (!mesh)
                        return false;
                    building.mouseEnter = function () {
                        if (activeCamera.current.isLock)
                            return false;
                        onBuildingEnter?.(name, id);
                    };
                    building.mouseLeave = function () {
                        if (activeCamera.current.isLock)
                            return false;
                        onBuildingLeave?.(name, id);
                    };
                    building.onClick = function () {
                        if (activeCamera.current.isLock)
                            return false;
                        currentBuilding.current = {
                            name,
                            index,
                            building,
                            cameraFocusSetting,
                        };
                        activeCamera.current.focus(building, cameraFocusSetting);
                        onBuildingClick?.(name, id);
                    };
                    if (textures.length) {
                        textures = getTextureList(textures, building);
                    }
                    if (subMeshes.length) {
                        subMeshes = getSubmeshes(subMeshes, building, {
                            id,
                            name,
                            x: building.position.x,
                            y: building.position.y,
                            z: building.position.z,
                        });
                    }
                }
                return {
                    id,
                    name,
                    building,
                    textures,
                    subMeshes,
                    points: [],
                    meshes: [],
                };
            });
            getPipelineList(pipelines);
            let pointList = getPointsList(points, sceneData.camera);
            currentBuildingList.current = { buildingList, pointList };
            return () => {
                window.removeEventListener("resize", handleResize);
                engine.dispose();
            };
        };
        let cleanup;
        runAsync().then((fn) => {
            cleanup = fn;
        });
        return () => {
            if (cleanup)
                cleanup();
        };
    }, []);
    return (_jsx("div", { className: `bg-gradient-to-br from-blue-900/10 to-slate-900/20 backdrop-blur-sm ${className}`, children: _jsxs("div", { className: "relative h-full", children: [_jsx("canvas", { ref: canvasRef, className: "w-full h-full", style: { outline: "none" } }), compassVisible && (_jsxs("div", { className: "absolute z-10 compass bottom-2 right-2", style: { transform: `rotate(${cameraAlpha + 180}deg)` }, children: [_jsx("span", { style: { transform: `rotate(${-cameraAlpha}deg)` }, children: "S" }), _jsx("span", { style: { transform: `rotate(${-cameraAlpha}deg)` }, children: "N" })] }))] }) }));
});
export default AssetMap3D;
